import { supabase } from "../lib/supabase";
import type { CostCenter } from "../types";
import { loadValidationQueue } from "./validations";

export type CenterReprogrammingStatus = "not_started" | "draft" | "closed" | "reopened" | "pending_gmr" | "pending_line" | "observed" | "accepted";

export interface CenterStatusRow {
  centerId: string;
  code: string;
  name: string;
  submissionId: string | null;
  status: CenterReprogrammingStatus;
  changedTargets: number;
  closedAt: string | null;
  reopenedAt: string | null;
  reopenReason: string | null;
}

interface SubmissionRow {
  id: string;
  cost_center_id: string;
  status: "draft" | "closed";
  closed_at: string | null;
  reopened_at: string | null;
  reopen_reason: string | null;
}

interface ChangeRow {
  submission_id: string;
}

function requireClient() {
  if (!supabase) throw new Error("Supabase no está configurado.");
  return supabase;
}

export async function loadCenterStatuses(cycleId: string, centers: CostCenter[]): Promise<CenterStatusRow[]> {
  const client = requireClient();
  const [{ data: submissionData, error: submissionError }, { data: changeData, error: changeError }, validationRows] = await Promise.all([
    client
      .from("submissions")
      .select("id, cost_center_id, status, closed_at, reopened_at, reopen_reason")
      .eq("cycle_id", cycleId),
    client.from("target_changes").select("submission_id"),
    loadValidationQueue(cycleId),
  ]);
  if (submissionError) throw submissionError;
  if (changeError) throw changeError;

  const submissions = (submissionData ?? []) as SubmissionRow[];
  const changes = (changeData ?? []) as ChangeRow[];
  const countBySubmission = new Map<string, number>();
  for (const change of changes) {
    countBySubmission.set(change.submission_id, (countBySubmission.get(change.submission_id) ?? 0) + 1);
  }

  return centers.map((center) => {
    const submission = submissions.find((item) => item.cost_center_id === center.id);
    const validations = validationRows.filter((item) => item.centerCode === center.code);
    const validationStatus: CenterReprogrammingStatus | null = !validations.length ? null
      : validations.some((item) => item.gmrStatus === "observed" || item.lineStatus === "observed") ? "observed"
        : validations.some((item) => item.gmrStatus === "pending") ? "pending_gmr"
          : validations.some((item) => item.lineStatus === "pending" || item.lineStatus === "waiting") ? "pending_line"
            : "accepted";
    const status: CenterReprogrammingStatus = !submission
      ? "not_started"
      : validationStatus === "observed"
        ? "observed"
        : submission.status === "closed"
          ? validationStatus ?? "closed"
          : submission.reopened_at
            ? "reopened"
            : "draft";
    return {
      centerId: center.id,
      code: center.code,
      name: center.name,
      submissionId: submission?.id ?? null,
      status,
      changedTargets: submission ? countBySubmission.get(submission.id) ?? 0 : 0,
      closedAt: submission?.closed_at ?? null,
      reopenedAt: submission?.reopened_at ?? null,
      reopenReason: submission?.reopen_reason ?? null,
    };
  });
}

export async function reopenCenter(submissionId: string, reason: string, months: number[]) {
  const client = requireClient();
  const { error } = await client.rpc("reopen_submission", {
    p_submission_id: submissionId,
    p_reason: reason,
    p_months: months,
  });
  if (error) throw error;
}

export async function finalizeCycle(cycleId: string) {
  const client = requireClient();
  const { data, error } = await client.rpc("finalize_reprogramming_cycle", {
    p_cycle_id: cycleId,
  });
  if (error) throw error;
  return Number(data);
}
